---
title: Sourcing locally
date: 2026-01-22
author: Brian Ellison
category: Materials
excerpt: An argument for sourcing within a half-day drive — what it costs, what it returns, and why the slowness is part of the work.
placeholder: true
---

A project that ships materials from four continents is a project that has outsourced its character.

We try not to do that.

## What we mean by local

Not literally. We are not going to mill our own marble in Brevard County. But there is a wide band of project material — finishes, hardware, custom millwork, ceramic, hand-pulled lighting, upholstery — that can be sourced within a half-day drive of Melbourne. We try to.

This means:
- **Plaster**: trowelled by an artisan in Saint Augustine.
- **Custom millwork**: a small shop in Cocoa.
- **Brass hardware**: a metalsmith in Saint Petersburg.
- **Linen + bouclé**: a textile studio in Sarasota.
- **Reclaimed wood**: salvage warehouse in Tampa.
- **Lighting** (custom): two makers in Asheville (a stretch, but in season).

When something genuinely cannot be sourced within range — Carrara marble, Welsh slate, certain Japanese ceramics — we go further. But we make sure the reach is necessary.

## What it costs

More money. More time. More phone calls. The half-day drive turns into two half-day drives if a sample arrives wrong. The metalsmith does not have a website; he has a voicemail.

This is a real cost, and we explain it to clients early.

## What it returns

Two things.

First, **objects with people behind them**. The brass pulls on a kitchen are not "the brass pulls"; they are *Sam Hendricks's* brass pulls. The plaster wall is the work of a man who has trowelled twelve thousand square feet of plaster in his career and recognizes our voicemails by the second word. The provenance is real, and the client feels it, even if they never meet any of these makers.

Second, **a project that holds up**. Local makers we have worked with for years know how we specify. They warn us when something we have asked for will fail. They suggest alternatives. They show up to the site if a finish is contested. The relationship is not transactional; it is collaborative. The work reflects that.

## Why this is not a marketing position

Some studios talk about local sourcing because it is a useful credential.

We talk about it because **a house built from materials whose makers we cannot name is a house we cannot stand behind**.

That is the entirety of the argument.

*A practice note. More to follow.*
